import { cloneDeep } from 'lodash-es';
import type { Site } from '@hotjar/webapp/domains/common/feature';
import { isAllowed } from '@hotjar/feature-flags';
import type {
  Segment,
  SegmentWithUnsupportedFeature,
} from '@hotjar/webapp/features/Recordings/types';
import type { SiteId } from '@hotjar/site';
import { convertFromQueryToNewUiFormat } from '@hotjar/global-filters';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import { getSettings, loadWindowSettings } from '@hotjar/settings';
import { getAll } from './api';
import { PREDEFINED_SEGMENTS } from './constant';

export const SEGMENTS_QUERY_KEY = 'segments';

const formatSegments = (userSegments: Segment[], site: Site): SegmentWithUnsupportedFeature[] => {
  const convertedUserSegments: Segment[] = userSegments.map((segment) => ({
    ...segment,
    unsupported_features: [],
  }));

  const predefinedSegments: Segment[] = cloneDeep(PREDEFINED_SEGMENTS);
  const segments: Segment[] = predefinedSegments.concat(convertedUserSegments);
  return segments.map((oneSegment) => {
    const filtersUiFormat = convertFromQueryToNewUiFormat(oneSegment.filters);
    const unsupportedFeatures = filtersUiFormat.filterList
      .map((oneFilter) => {
        return `filters.${oneFilter.kind}`;
      })
      .filter((oneFeature) => {
        return !isAllowed(oneFeature, site);
      });
    return {
      ...oneSegment,
      unsupported_features: unsupportedFeatures,
    };
  });
};

export async function getAllSegments(
  siteId: SiteId,
  site: Site,
): Promise<SegmentWithUnsupportedFeature[]> {
  const userSegments = await queryClient.fetchQuery(
    [SEGMENTS_QUERY_KEY, { siteId }],
    () => getAll(siteId),
    {
      staleTime: 60 * 60 * 1000,
      cacheTime: 60 * 60 * 1000,
    },
  );
  // Hacky hack:
  // Format segment uses isAllowed method.
  // isAllowed method rely on having the setting being loaded in the window object
  // We need to make sure it is there
  if (!getSettings()) {
    await loadWindowSettings();
  }

  return formatSegments(userSegments, site);
}

export const getAllCachedSegments = (
  siteId: SiteId,
  site: Site,
): SegmentWithUnsupportedFeature[] | undefined => {
  const userSegments: Segment[] | undefined = queryClient.getQueryData([
    SEGMENTS_QUERY_KEY,
    { siteId },
  ]);
  if (!userSegments) {
    return undefined;
  }

  return formatSegments(userSegments, site);
};
